import { useEffect, useMemo, useState } from 'react';
import { addDays, format, parse, startOfDay, startOfWeek } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { ru } from 'date-fns/locale';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Modal } from '@shared/ui/modal/modal';
import { Button } from '@shared/ui/button/button';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { useProfile } from '@shared/api/useProfile';
import { useTeacherSchedule } from '../../api/use-teacher-schedule';
import { useSendSlotOffer } from '../../model/use-send-offer';
import { useSlotOfferModal } from '../../model/use-slot-offer-modal';
import s from './slot-offer-modal.module.scss';

const RU_SHORT_WEEKDAY = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const DATE_FORMAT = 'dd.MM.yyyy';

const normalizeTime = (time) => {
  if (!time || typeof time !== 'string') return null;
  const [h, m] = time.split(':');
  if (h == null || m == null) return null;
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
};

const isValidTimeString = (time) => {
  const normalized = normalizeTime(time);
  if (!normalized) return false;
  const [h, m] = normalized.split(':').map(Number);
  return h >= 0 && h < 24 && m >= 0 && m < 60;
};

// Модалка отправки предложения слота: инструмент + дата + свободное время
export const SlotOfferModal = () => {
  const { isOpen, close, modalParams } = useSlotOfferModal();
  const chatId = modalParams?.chatId;

  const { data: profile } = useProfile();
  const teacherSlug = profile?.teacher_slug;
  const sendSlotOffer = useSendSlotOffer(chatId);

  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const today = useMemo(() => {
    if (!isOpen) return startOfDay(new Date());
    // Все расчёты дней — в таймзоне профиля преподавателя,
    // чтобы слоты недели совпадали с его расписанием
    return startOfDay(new TZDate(new Date(), profile?.timezone));
  }, [isOpen, profile?.timezone]);

  const initialWeekStart = useMemo(
    () => startOfWeek(today, { weekStartsOn: 1 }),
    [today]
  );

  const weekStartState = useState(initialWeekStart);
  const weekStart = weekStartState[0];
  const setWeekStart = weekStartState[1];

  // При открытии модалки сбрасываем неделю на текущую (в таймзоне профиля)
  useEffect(() => {
    if (isOpen) {
      setWeekStart(startOfWeek(startOfDay(new TZDate(new Date(), profile?.timezone)), { weekStartsOn: 1 }));
    }
  }, [isOpen, profile?.timezone, setWeekStart]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedInstrument(profile?.instruments?.[0]?.id ?? null);
    setSelectedDate(null);
    setSelectedTime(null);
  }, [isOpen, profile?.instruments]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const weekLabel = `${format(weekStart, 'd MMMM', { locale: ru })} – ${format(
    addDays(weekStart, 6),
    'd MMMM',
    { locale: ru }
  )}`;

  const weeklyParams = useMemo(
    () => ({
      date_from: format(weekStart, DATE_FORMAT),
      date_to: format(addDays(weekStart, 6), DATE_FORMAT),
    }),
    [weekStart]
  );

  const { data: weekSchedule } = useTeacherSchedule(teacherSlug, weeklyParams);

  // Дни недели, в которых есть хотя бы один свободный слот
  const availableWeekdays = useMemo(() => {
    const set = new Set();
    (weekSchedule || []).forEach((el) => {
      if (isValidTimeString(el.time) && !el.reserved) set.add(el.weekday);
    });
    return set;
  }, [weekSchedule]);

  const dailyParams = useMemo(
    () =>
      selectedDate
        ? { date_from: selectedDate, date_to: selectedDate }
        : null,
    [selectedDate]
  );

  const { data: daySchedule, isLoading: isDayLoading } = useTeacherSchedule(
    teacherSlug,
    dailyParams
  );

  const freeSlots = useMemo(
    () =>
      (daySchedule || [])
        .filter((el) => isValidTimeString(el.time) && !el.reserved)
        .map((el) => normalizeTime(el.time))
        .sort(),
    [daySchedule]
  );

  const canPrevWeek = weekStart.getTime() > startOfWeek(today, { weekStartsOn: 1 }).getTime();
  const handleSelectDay = (day) => {
    const dateString = format(day, DATE_FORMAT);
    if (dateString === selectedDate) return;
    setSelectedDate(dateString);
    setSelectedTime(null);
  };

  const handleSend = () => {
    const instrument = profile?.instruments?.find(
      (i) => i.id === selectedInstrument
    );
    // Бэк ожидает сырую дату в формате ISO (2006-01-02),
    // внутри модалки и для API расписания используем dd.MM.yyyy
    const dateObj = parse(selectedDate, DATE_FORMAT, new Date());
    sendSlotOffer({
      date: format(dateObj, 'yyyy-MM-dd'),
      time: normalizeTime(selectedTime),
      instrument_id: selectedInstrument,
      instrument_name: instrument?.name,
    });
    close();
  };

  if (!isOpen) return null;

  const selectedDateLabel = selectedDate
    ? format(parse(selectedDate, DATE_FORMAT, new Date()), 'd MMMM', { locale: ru })
    : null;

  const modalStyle = {
    content: {
      width: 'calc(100% - 40px)',
      maxWidth: '420px',
      maxHeight: 'calc(100svh - 40px)',
      padding: '20px',
      overflow: 'auto',
    },
  };

  return (
    <Modal {...{ isOpen, close }} title="Предложить слот" style={modalStyle}>
      <motion.div
        className={s.card}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        <div className={s.sectionCard}>
          <h3 className={s.sectionTitle}>Инструмент</h3>
          <div className={s.instruments}>
            {profile?.instruments?.map((el) => (
              <button
                key={el.id}
                type="button"
                className={classNames(
                  s.instrumentButton,
                  selectedInstrument === el.id && s.active
                )}
                onClick={() => setSelectedInstrument(el.id)}
              >
                <TextWithEmoji text={el.name} />
              </button>
            ))}
          </div>
        </div>

        <div className={s.sectionCard}>
          <h3 className={s.sectionTitle}>Дата</h3>
          <div className={s.weekNav}>
            <button
              type="button"
              className={s.weekArrow}
              onClick={() => setWeekStart((prev) => addDays(prev, -7))}
              disabled={!canPrevWeek}
              aria-label="Предыдущая неделя"
            >
              <IconChevronLeft size={20} />
            </button>
            <span className={s.weekLabel}>{weekLabel}</span>
            <button
              type="button"
              className={s.weekArrow}
              onClick={() => setWeekStart((prev) => addDays(prev, 7))}
              aria-label="Следующая неделя"
            >
              <IconChevronRight size={20} />
            </button>
          </div>
          <div className={s.daysRow}>
            {weekDays.map((day) => {
              const weekday = day
                .toLocaleDateString('en-us', { weekday: 'long' })
                .toLowerCase();
              // Минимальная дата бронирования — завтра (как на странице
              // бронирования), поэтому сегодняшний день тоже недоступен
              const isPast =
                startOfDay(day).getTime() <= today.getTime();
              const hasSlots = availableWeekdays.has(weekday);
              const disabled = isPast || !hasSlots;
              const dateString = format(day, DATE_FORMAT);

              return (
                <button
                  key={dateString}
                  type="button"
                  className={classNames(
                    s.day,
                    selectedDate === dateString && s.active,
                    disabled && s.disabled
                  )}
                  onClick={() => handleSelectDay(day)}
                  disabled={disabled}
                >
                  <span className={s.dayWeekday}>{RU_SHORT_WEEKDAY[day.getDay()]}</span>
                  <span className={s.dayNumber}>{format(day, 'd')}</span>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDate && (
          <div className={s.sectionCard}>
            <h3 className={s.sectionTitle}>Время</h3>
            {isDayLoading ? (
              <span className={s.emptySlots}>Загрузка…</span>
            ) : freeSlots.length > 0 ? (
              <div className={s.slots}>
                {freeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    className={classNames(s.slot, selectedTime === time && s.active)}
                    onClick={() => setSelectedTime(time)}
                  >
                    {time}
                  </button>
                ))}
              </div>
            ) : (
              <span className={s.emptySlots}>Нет свободного времени на этот день</span>
            )}
          </div>
        )}

        {(selectedDateLabel || selectedTime) && (
          <div className={s.summary}>
            {selectedDateLabel && (
              <span className={s.summaryTag}>{selectedDateLabel}</span>
            )}
            {selectedTime && (
              <span className={s.summaryTag}>{selectedTime}</span>
            )}
          </div>
        )}

        <div className={s.footer}>
          <Button
            label="Отправить предложение"
            onClick={handleSend}
            disabled={!selectedDate || !selectedTime || !selectedInstrument}
            className={s.submitBtn}
          />
        </div>
      </motion.div>
    </Modal>
  );
};
